import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faList,
  faTableCells,
  faChartDiagram,
  faNewspaper,
  faLayerGroup,
  faFilePen,
  faPeopleRoof,
  faWeightScale,
  faBuildingColumns,
  faUsers,
  faCity,
  faChartPie,
  faInfoCircle,
  faChartLine,
  faHeart,
  faCaretDown,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  EVALUATE_CHART_COLORS,
  EVALUATE_COMPANY,
  EVALUATE_FINANCIAL_PERIODS,
  EVALUATE_NAV,
  EVALUATE_PRICE,
  EVALUATE_SPARKLINE,
  EVALUATE_STATEMENT_ROWS,
} from '../heroTheaterData';

interface EvaluateSceneProps {
  progress: number;
  reducedMotion: boolean;
}

const NAV_ICONS: Record<(typeof EVALUATE_NAV)[number]['id'], IconDefinition> = {
  'key-stats': faList,
  financials: faTableCells,
  snapshots: faChartDiagram,
  transcripts: faNewspaper,
  'earn-trend': faLayerGroup,
  filings: faFilePen,
  insiders: faPeopleRoof,
  analysts: faWeightScale,
  institutions: faBuildingColumns,
  leadership: faUsers,
  competitors: faCity,
  shares: faChartPie,
};

const TIMEFRAMES = ['1D', '1M', '6M', 'YTD', '1Y', '5Y', 'MAX'] as const;
const MAX_REVENUE = Math.max(...EVALUATE_FINANCIAL_PERIODS.map((p) => p.revenue));

function clamp01(n: number) {
  return Math.min(1, Math.max(0, n));
}

function densityClass(density: 'always' | 'md' | 'lg') {
  if (density === 'lg') return 'hidden lg:flex';
  if (density === 'md') return 'hidden md:flex';
  return 'flex';
}

function densityCellClass(density: 'always' | 'md' | 'lg') {
  if (density === 'lg') return 'hidden lg:table-cell';
  if (density === 'md') return 'hidden md:table-cell';
  return 'table-cell';
}

function TickerLogo({ size = 'md' }: { size?: 'sm' | 'md' }) {
  const sizeClass =
    size === 'sm'
      ? 'h-6 min-w-6 px-1 text-[7px]'
      : 'h-8 min-w-8 px-1.5 text-[9px] sm:h-9 sm:min-w-9 sm:text-[10px] md:h-10 md:min-w-10 md:text-[11px]';
  return (
    <div
      className={`flex shrink-0 items-center justify-center rounded-lg font-bold tracking-tight text-white ring-1 ring-black/10 ${sizeClass}`}
      style={{
        background: `linear-gradient(145deg, ${EVALUATE_COMPANY.logoTone}, #3d5c00)`,
      }}
      aria-hidden
    >
      {EVALUATE_COMPANY.symbol}
    </div>
  );
}

function sparklinePaths(values: number[], w: number, h: number, pad = 3) {
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const pts = values.map((v, i) => {
    const x = pad + (i / (values.length - 1)) * (w - pad * 2);
    const y = pad + (1 - (v - min) / span) * (h - pad * 2);
    return [x, y] as const;
  });
  const line = pts
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0].toFixed(1)} ${p[1].toFixed(1)}`)
    .join(' ');
  const last = pts[pts.length - 1];
  const first = pts[0];
  const area = `${line} L ${last[0].toFixed(1)} ${h} L ${first[0].toFixed(1)} ${h} Z`;
  return { line, area };
}

function Sparkline({ drawn, reducedMotion }: { drawn: boolean; reducedMotion: boolean }) {
  const { line, area } = sparklinePaths(EVALUATE_SPARKLINE, 100, 36);
  return (
    <svg viewBox="0 0 100 36" className="h-full w-full" preserveAspectRatio="none" aria-hidden>
      <motion.path
        d={area}
        fill="rgba(244, 63, 94, 0.12)"
        initial={false}
        animate={{ opacity: drawn ? 1 : 0 }}
        transition={{ duration: reducedMotion ? 0 : 0.2, delay: reducedMotion ? 0 : 0.28 }}
      />
      <motion.path
        d={line}
        fill="none"
        stroke="#f43f5e"
        strokeWidth="0.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={false}
        animate={{ pathLength: drawn ? 1 : 0, opacity: drawn ? 1 : 0 }}
        transition={{ duration: reducedMotion ? 0 : 0.4, ease: 'easeOut' }}
      />
    </svg>
  );
}

function BreakdownView({
  progress,
  reducedMotion,
}: {
  progress: number;
  reducedMotion: boolean;
}) {
  const identityOn = true;
  const chartOn = reducedMotion || progress > 0.08;
  const navPhase = reducedMotion ? 1 : clamp01((progress - 0.18) / 0.28);
  const financialsHot = reducedMotion || progress >= 0.48;

  const columns = [0, 1, 2].map((col) =>
    EVALUATE_NAV.map((item, i) => ({ item, i })).filter(({ i }) => i % 3 === col),
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Identity */}
      <motion.div
        className="relative mb-1 flex shrink-0 flex-col items-center text-center sm:mb-1.5"
        initial={reducedMotion ? false : { opacity: 0, y: 8 }}
        animate={identityOn ? { opacity: 1, y: 0 } : { opacity: 0, y: 8 }}
        transition={{ duration: 0.22 }}
      >
        <div className="absolute right-0 top-0 flex flex-col items-center">
          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-800 text-[8px] font-bold text-white ring-2 ring-white sm:h-8 sm:w-8 sm:text-[9px]">
            3/3
          </div>
          <span className="mt-0.5 text-[8px] font-semibold text-gray-500 sm:text-[9px]">
            Match ›
          </span>
        </div>
        <TickerLogo />
        <p className="mt-1.5 text-[13px] font-bold leading-tight text-gray-900 sm:mt-2 sm:text-sm md:text-base">
          {EVALUATE_COMPANY.name}
        </p>
        <p className="mt-0.5 text-[10px] sm:text-[11px]">
          <span className="font-extrabold text-emerald-700">{EVALUATE_COMPANY.symbol}</span>
          <span className="ml-1 font-medium text-gray-400">({EVALUATE_COMPANY.exchange})</span>
        </p>
      </motion.div>

      {/* Action pills + favorite */}
      <motion.div
        className="mb-1.5 flex shrink-0 items-center justify-center gap-1 sm:mb-2 sm:gap-1.5"
        initial={reducedMotion ? false : { opacity: 0 }}
        animate={identityOn ? { opacity: 1 } : { opacity: 0 }}
      >
        {[
          { icon: faInfoCircle, label: 'About', short: 'About' },
          { icon: faNewspaper, label: 'News', short: 'News' },
          { icon: faChartLine, label: 'Price Forecast', short: 'Forecast' },
        ].map((tab) => (
          <span
            key={tab.label}
            className="inline-flex items-center gap-1 rounded-full bg-white px-1.5 py-0.5 text-[8px] font-semibold text-gray-700 ring-1 ring-black/[0.08] sm:px-2 sm:py-1 sm:text-[9px] md:text-[10px]"
          >
            <FontAwesomeIcon icon={tab.icon} className="h-[0.7em] w-[0.7em] text-gray-500" />
            <span className="sm:hidden">{tab.short}</span>
            <span className="hidden sm:inline">{tab.label}</span>
          </span>
        ))}
        <span className="ml-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-white ring-1 ring-black/[0.06] sm:h-7 sm:w-7">
          <FontAwesomeIcon icon={faHeart} className="text-[10px] text-rose-500 sm:text-[11px]" />
        </span>
      </motion.div>

      {/* Price + sparkline */}
      <motion.div
        className="mb-2 flex min-h-[72px] w-full shrink-0 flex-col rounded-xl bg-white px-2 py-1.5 ring-1 ring-black/[0.05] sm:mb-2.5 sm:min-h-[84px] sm:px-2.5 sm:py-2 md:mx-auto md:min-h-[96px] md:max-h-[128px] md:w-[92%] md:flex-1"
        initial={reducedMotion ? false : { opacity: 0, y: 8 }}
        animate={chartOn ? { opacity: 1, y: 0 } : { opacity: 0, y: 8 }}
        transition={{ duration: 0.22 }}
      >
        <div className="mb-0.5 flex items-center gap-1.5 sm:mb-1">
          <span className="text-[13px] font-bold tabular-nums text-gray-900 sm:text-sm md:text-base">
            {EVALUATE_PRICE.last}
          </span>
          <span className="inline-flex items-center gap-0.5 rounded-md bg-rose-50 px-1 py-0.5 text-[8px] font-semibold tabular-nums text-rose-600 sm:text-[9px] md:text-[10px]">
            <FontAwesomeIcon icon={faCaretDown} className="text-[8px]" />
            {EVALUATE_PRICE.change}
          </span>
        </div>
        <div className="relative min-h-0 flex-1">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex flex-col justify-between py-0.5 text-[7px] font-medium tabular-nums text-gray-400 sm:text-[8px]">
            {EVALUATE_PRICE.axis.map((label) => (
              <span key={label}>{label}</span>
            ))}
          </div>
          <div className="h-full pl-5 sm:pl-6">
            <Sparkline drawn={chartOn} reducedMotion={reducedMotion} />
          </div>
        </div>
        <div className="mt-1 flex items-center gap-0.5 pl-5 sm:gap-1 sm:pl-6">
          {TIMEFRAMES.map((tf) => {
            const hide =
              tf === '6M' || tf === 'YTD' || tf === '5Y'
                ? 'hidden sm:inline-flex'
                : 'inline-flex';
            const active = tf === '1D';
            return (
              <span
                key={tf}
                className={`${hide} items-center rounded-full px-1.5 py-0.5 text-[7px] font-bold sm:text-[8px] md:px-2 md:text-[9px] ${
                  active ? 'bg-gray-900 text-white' : 'text-gray-400'
                }`}
              >
                {tf}
              </span>
            );
          })}
        </div>
      </motion.div>

      {/* Breakdown rails */}
      <div
        className="grid min-h-0 shrink-0 grid-cols-3 gap-1 sm:gap-2 md:gap-3"
        style={{ opacity: reducedMotion || navPhase > 0.02 ? 1 : 0 }}
      >
        {columns.map((colItems, col) => (
          <div key={col} className="relative min-w-0">
            <div
              className="absolute bottom-1 left-[7px] top-1 w-px sm:left-[9px] md:left-[11px]"
              style={{
                backgroundImage:
                  'repeating-linear-gradient(to bottom, transparent, transparent 3px, #c4c4bc 3px, #c4c4bc 6px)',
              }}
              aria-hidden
            />
            <div className="space-y-5 sm:space-y-7">
              {colItems.map(({ item, i }, row) => {
                const visibleAt = (i + 1) / (EVALUATE_NAV.length + 1);
                const show = reducedMotion || navPhase >= visibleAt * 0.85;
                const isFinancials = item.id === 'financials';
                const highlighted = isFinancials && financialsHot && show;
                const rowClass = row >= 3 ? 'flex md:hidden' : 'flex';

                return (
                  <motion.div
                    key={item.id}
                    className={`${rowClass} relative min-w-0 items-center`}
                    initial={reducedMotion ? false : { opacity: 0, x: -6 }}
                    animate={show ? { opacity: 1, x: 0 } : { opacity: 0, x: -6 }}
                    transition={{ duration: 0.18, delay: reducedMotion ? 0 : i * 0.018 }}
                  >
                    <span
                      className={`absolute left-[7px] z-10 h-1.5 w-1.5 -translate-x-1/2 rounded-full border border-white sm:left-[9px] sm:h-2 sm:w-2 md:left-[11px] ${
                        highlighted ? 'bg-emerald-600' : 'bg-gray-700'
                      }`}
                      aria-hidden
                    />
                    <div
                      className={`ml-3.5 flex min-w-0 flex-1 items-center gap-1 rounded-lg py-0.5 pl-0.5 sm:ml-4 sm:gap-1.5 md:ml-5 ${
                        highlighted ? 'bg-emerald-50 ring-1 ring-emerald-300' : ''
                      }`}
                    >
                      <span
                        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full sm:h-6 sm:w-6 md:h-7 md:w-7 ${
                          highlighted ? 'bg-emerald-700' : 'bg-black'
                        }`}
                      >
                        <FontAwesomeIcon
                          icon={NAV_ICONS[item.id]}
                          className={`text-[8px] text-white sm:text-[9px] md:text-[11px] ${
                            item.id === 'snapshots' ? '-rotate-90' : ''
                          }`}
                        />
                      </span>
                      <span className="min-w-0 truncate text-[8px] font-semibold text-gray-800 sm:text-[9px] md:text-[11px]">
                        <span className="lg:hidden">{item.shortLabel}</span>
                        <span className="hidden lg:inline">{item.label}</span>
                      </span>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FinancialsView({
  progress,
  reducedMotion,
}: {
  progress: number;
  reducedMotion: boolean;
}) {
  const barsPhase = reducedMotion ? 1 : clamp01((progress - 0.68) / 0.16);
  const tableOn = reducedMotion || progress >= 0.78;

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Header */}
      <div className="mb-1 flex shrink-0 items-center justify-center gap-1.5 sm:mb-1.5">
        <p className="text-[12px] font-bold text-gray-900 sm:text-sm md:text-base">Financials</p>
        <TickerLogo size="sm" />
        <span
          className="text-[10px] font-extrabold text-emerald-700 sm:text-[11px]"
          aria-label={EVALUATE_COMPANY.symbol}
        >
          {EVALUATE_COMPANY.symbol}
        </span>
      </div>

      {/* Trend bars */}
      <div className="mb-1.5 flex shrink-0 flex-col rounded-xl bg-white px-2 pb-1.5 pt-5 ring-1 ring-black/[0.05] sm:mb-2 sm:px-3 sm:pt-6">
        <div className="flex items-end justify-around gap-1 px-1">
          {EVALUATE_FINANCIAL_PERIODS.map((period, i) => {
            const show = reducedMotion || barsPhase > i / (EVALUATE_FINANCIAL_PERIODS.length + 0.4);
            const revPct = (period.revenue / MAX_REVENUE) * 78;
            const cogsPct = (period.cogs / MAX_REVENUE) * 78;
            const showValue = period.density !== 'lg';

            return (
              <div
                key={period.id}
                className={`${densityClass(period.density)} min-w-0 flex-1 flex-col items-center`}
              >
                <div className="relative flex h-[64px] w-full items-end justify-center gap-0.5 sm:h-[76px] md:h-[88px] md:gap-1">
                  {showValue && (
                    <motion.span
                      className="absolute left-1/2 z-10 -translate-x-1/2 text-[7px] font-bold tabular-nums text-gray-700 sm:text-[8px] md:text-[9px]"
                      style={{ bottom: `calc(${revPct}% + 2px)` }}
                      initial={false}
                      animate={{ opacity: show ? 1 : 0 }}
                    >
                      {period.revLabel}
                    </motion.span>
                  )}
                  <motion.div
                    className="w-[6px] rounded-t-[3px] sm:w-[8px] md:w-[11px]"
                    style={{
                      backgroundColor: period.id === 'ttm'
                        ? `${EVALUATE_CHART_COLORS.revenue}59`
                        : EVALUATE_CHART_COLORS.revenue,
                      outline: period.id === 'ttm'
                        ? `1.5px dashed ${EVALUATE_CHART_COLORS.revenue}`
                        : undefined,
                      outlineOffset: period.id === 'ttm' ? '-1px' : undefined,
                    }}
                    initial={false}
                    animate={{ height: show ? `${revPct}%` : '0%' }}
                    transition={{ duration: reducedMotion ? 0 : 0.28, ease: 'easeOut' }}
                  />
                  <motion.div
                    className="w-[6px] rounded-t-[3px] sm:w-[8px] md:w-[11px]"
                    style={{
                      backgroundColor: period.id === 'ttm'
                        ? `${EVALUATE_CHART_COLORS.cogs}59`
                        : EVALUATE_CHART_COLORS.cogs,
                      outline: period.id === 'ttm'
                        ? `1.5px dashed ${EVALUATE_CHART_COLORS.cogs}`
                        : undefined,
                      outlineOffset: period.id === 'ttm' ? '-1px' : undefined,
                    }}
                    initial={false}
                    animate={{ height: show ? `${cogsPct}%` : '0%' }}
                    transition={{
                      duration: reducedMotion ? 0 : 0.28,
                      delay: reducedMotion ? 0 : 0.04,
                      ease: 'easeOut',
                    }}
                  />
                </div>
                <span className="mt-1 text-[8px] font-semibold text-gray-500 sm:text-[9px] md:text-[10px]">
                  {period.year}
                </span>
              </div>
            );
          })}
        </div>
        <div className="mt-1 flex items-center justify-center gap-3">
          <span className="inline-flex items-center gap-1 text-[8px] font-medium text-gray-600 sm:text-[9px]">
            <span
              className="h-1.5 w-1.5 rounded-full"
              style={{ backgroundColor: EVALUATE_CHART_COLORS.revenue }}
            />
            Revenue
          </span>
          <span className="inline-flex items-center gap-1 text-[8px] font-medium text-gray-600 sm:text-[9px]">
            <span
              className="h-1.5 w-1.5 rounded-full"
              style={{ backgroundColor: EVALUATE_CHART_COLORS.cogs }}
            />
            Cost of Revenue
          </span>
        </div>
        <div className="mt-1 flex justify-center">
          <span className="rounded-full bg-gray-900 px-2 py-0.5 text-[8px] font-bold text-white sm:text-[9px]">
            5 Years
          </span>
        </div>
      </div>

      {/* Statement table */}
      <motion.div
        className="min-h-0 flex-1 overflow-hidden rounded-xl bg-white ring-1 ring-black/[0.05]"
        initial={reducedMotion ? false : { opacity: 0, y: 10 }}
        animate={tableOn ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
        transition={{ duration: 0.22 }}
      >
        <div className="flex items-center justify-between gap-2 border-b border-black/[0.06] px-2 py-1 sm:px-2.5">
          <div className="flex items-center gap-2 text-[8px] font-semibold sm:text-[9px] md:text-[10px]">
            <span className="border-b-2 border-gray-900 pb-0.5 text-gray-900">Income Statement</span>
            <span className="hidden text-gray-400 sm:inline">Balance Sheet</span>
            <span className="hidden text-gray-400 md:inline">Cash Flow</span>
          </div>
          <span className="inline-flex items-center gap-1 rounded-full bg-gray-900 px-1.5 py-0.5 text-[7px] font-bold text-white sm:text-[8px]">
            YoY
          </span>
        </div>
        <table className="w-full table-fixed border-collapse text-left">
          <thead>
            <tr className="text-[7px] font-semibold text-gray-400 sm:text-[8px] md:text-[9px]">
              <th className="w-[28%] px-1.5 py-0.5 font-semibold sm:w-[30%] sm:px-2"> </th>
              {EVALUATE_FINANCIAL_PERIODS.map((period) => (
                <th
                  key={period.id}
                  className={`${densityCellClass(period.density)} px-0.5 py-0.5 text-center font-semibold`}
                >
                  {period.year}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {EVALUATE_STATEMENT_ROWS.map((row, ri) => {
              const show = reducedMotion || tableOn;
              const isRev = row.tone === 'revenue';
              const isCogs = row.tone === 'cogs';
              const rowBg = isRev
                ? EVALUATE_CHART_COLORS.revenue
                : isCogs
                  ? EVALUATE_CHART_COLORS.cogs
                  : undefined;
              const hideRow = ri >= 4 ? 'hidden md:table-row' : undefined;
              return (
                <motion.tr
                  key={row.id}
                  className={`text-[7px] sm:text-[8px] md:text-[9px] ${hideRow ?? ''}`}
                  initial={false}
                  animate={{ opacity: show ? 1 : 0 }}
                  transition={{ delay: reducedMotion ? 0 : ri * 0.03 }}
                >
                  <td
                    className={`truncate px-1.5 py-1 font-semibold sm:px-2 ${
                      isRev ? 'text-white' : 'text-gray-900'
                    }`}
                    style={{ backgroundColor: rowBg }}
                  >
                    {'shortLabel' in row ? (
                      <>
                        <span className="md:hidden">{row.shortLabel}</span>
                        <span className="hidden md:inline">{row.label}</span>
                      </>
                    ) : (
                      row.label
                    )}
                  </td>
                  {EVALUATE_FINANCIAL_PERIODS.map((period, pi) => (
                    <td
                      key={period.id}
                      className={`${densityCellClass(period.density)} px-0.5 py-1 text-center tabular-nums`}
                      style={{ backgroundColor: rowBg }}
                    >
                      <div
                        className={`font-semibold ${isRev ? 'text-white' : 'text-gray-900'}`}
                      >
                        {row.values[pi]}
                      </div>
                      <div
                        className={`text-[6px] font-medium sm:text-[7px] ${
                          isRev ? 'text-white/85' : 'text-emerald-700'
                        }`}
                      >
                        {row.yoy[pi]} YoY
                      </div>
                    </td>
                  ))}
                </motion.tr>
              );
            })}
          </tbody>
        </table>
      </motion.div>
    </div>
  );
}

/**
 * Ticker breakdown assembling, then a drill-in to financial trend visualization.
 */
export default function EvaluateScene({ progress, reducedMotion }: EvaluateSceneProps) {
  // Beat map:
  // 0–0.18: identity + price chart
  // 0.18–0.48: nav rails assemble
  // 0.48–0.66: Financials highlighted
  // 0.66–1.0: financials view (bars, then statement)
  const showFinancials = reducedMotion || progress >= 0.66;

  return (
    <div className="relative h-full w-full overflow-hidden bg-background px-2.5 pb-3 pt-8 sm:px-3.5 sm:pb-4 sm:pt-9 md:px-4 md:pt-4">
      <AnimatePresence mode="wait" initial={false}>
        {!showFinancials ? (
          <motion.div
            key="breakdown"
            className="h-full"
            initial={reducedMotion ? false : { opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={reducedMotion ? { opacity: 0 } : { opacity: 0, x: -18 }}
            transition={{ duration: 0.25, ease: [0.32, 0.72, 0, 1] }}
          >
            <BreakdownView progress={progress} reducedMotion={reducedMotion} />
          </motion.div>
        ) : (
          <motion.div
            key="financials"
            className="h-full"
            initial={reducedMotion ? false : { opacity: 0, x: 18 }}
            animate={{ opacity: 1, x: 0 }}
            exit={reducedMotion ? { opacity: 0 } : { opacity: 0, x: 12 }}
            transition={{ duration: 0.25, ease: [0.32, 0.72, 0, 1] }}
          >
            <FinancialsView progress={progress} reducedMotion={reducedMotion} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
